import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { EmptyArray } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import getPrioritizedBedbankAmenities from 'lib/offer/getPrioritizedBedbankAmenities'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import LuxLPPPricingLabeledSheet from 'luxPlus/components/LuxLPPPricingLabeledSheet'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import SearchTileStackAmenitiesList from '../shared/SearchTileStackAmenitiesList'
import SearchTileStackInclusionsList from '../shared/SearchTileStackInclusionsList'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import SearchTileFreeCancellationInclusion, {
  type SearchTileCancellationInclusionKind,
  useSearchTileCancellationInclusion,
} from '../shared/SearchTileFreeCancellationInclusion'
import { useActiveAmenityFilters } from 'hooks/useActiveAmenityFilters'
import { reorderAmenitiesByActiveFilters } from 'lib/offer/amenitiesUtils'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import { getCompactInclusionListLimit } from './accommodationSearchTileView'
import {
  resolveMappedSearchFilterInclusions,
  shouldShowCompactMappedInclusionsOverflow,
  shouldUseMappedSearchFilterInclusions,
  useSearchFilterCategoryInclusions,
} from '../shared/useSearchFilterCategoryInclusions'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  truncateAfter?: number
  singleColumn?: boolean
  hideLuxPlusPricingSheet?: boolean
  hideRoomBreakdown?: boolean
  maxAmenitiesToShow?: number
  hideLoyaltyInclusions?: boolean
  mapToSearchFilterCategories?: boolean
  cancellationInclusionKind?: SearchTileCancellationInclusionKind
}

function BedbankSearchTileFeatures({
  offer,
  filters = EmptyObject,
  truncateAfter,
  singleColumn,
  hideLuxPlusPricingSheet = false,
  hideRoomBreakdown = false,
  maxAmenitiesToShow,
  hideLoyaltyInclusions = false,
  mapToSearchFilterCategories = false,
  cancellationInclusionKind,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const activeAmenityFilters = useActiveAmenityFilters()
  const { offerMemberPrice, offerPrice, shownRate } = useBedbankOfferSearchPricing(offer, filters)
  const shownRateInclusions = useMemo(() => {
    const inclusions = shownRate?.inclusions
    if (!inclusions?.length) return EmptyArray
    return inclusions.filter(inclusion => inclusion.description !== 'Room only')
  }, [shownRate?.inclusions])
  const truncateAmenitiesAfter = maxAmenitiesToShow ?? truncateAfter
  const mappedInclusionLimit = truncateAmenitiesAfter ?? 6
  const cancellationInclusion = useSearchTileCancellationInclusion(cancellationInclusionKind)
  const mappedSearchFilterInclusions = useSearchFilterCategoryInclusions(
    mapToSearchFilterCategories ? shownRateInclusions : EmptyArray,
    mappedInclusionLimit,
  )
  const isUsingMappedRateInclusions = shouldUseMappedSearchFilterInclusions(
    mapToSearchFilterCategories,
    mappedSearchFilterInclusions.inclusions.length,
  )
  const amenities = useMemo<Array<App.OfferInclusion>>(() => {
    if (isUsingMappedRateInclusions) return EmptyArray

    const popularFacilities = activeAmenityFilters.size > 0
      ? reorderAmenitiesByActiveFilters(offer.popularFacilities, activeAmenityFilters)
      : offer.popularFacilities

    return getPrioritizedBedbankAmenities({
      shownRateInclusions: shownRate?.inclusions,
      popularFacilities,
      priorityVariant: 'default',
    })
  }, [activeAmenityFilters, isUsingMappedRateInclusions, offer.popularFacilities, shownRate?.inclusions])
  const displayedMappedInclusions = useMemo(() => {
    const mapped = resolveMappedSearchFilterInclusions(
      isUsingMappedRateInclusions,
      mappedSearchFilterInclusions.inclusions,
      shownRateInclusions,
    )
    if (!isUsingMappedRateInclusions || !cancellationInclusion) return mapped
    return [cancellationInclusion, ...mapped]
  }, [cancellationInclusion, isUsingMappedRateInclusions, mappedSearchFilterInclusions.inclusions, shownRateInclusions])
  const inclusionOverflowLabel = shouldShowCompactMappedInclusionsOverflow(
    isUsingMappedRateInclusions,
    shownRateInclusions.length,
    mappedSearchFilterInclusions.inclusions.length,
  ) ? mappedSearchFilterInclusions.showMoreLabel : undefined

  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionTo={getHotelOfferUrl(offer.id, offer.type)}
      primaryActionText={intl.formatMessage({
        id: '2szLgXhn8NOg1vMPBeWX',
        defaultMessage: 'View offer',
        description: 'CTA button in bedbank rate inclusions modal to view the offer from a compact search tile',
      })}
    >
      <Group direction="vertical" gap={4} noDisplayWhenEmpty>
        {!!shownRateInclusions.length && <OfferInclusionsList inclusions={shownRateInclusions} />}
      </Group>
    </Modal>)
  }, [intl, offer.id, offer.property.name, offer.type, showModal, shownRateInclusions])

  const luxPlusLPPEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const directSearchPrices = useDirectSearchPrices({ filters, offerId: offer.id })
  const amenitiesListVariant = mapToSearchFilterCategories ? 'small' as const : undefined
  return <Group direction="vertical" verticalAlign="space-between" gap={12} grow noDisplayWhenEmpty>
    <Group direction="vertical" gap={12} noDisplayWhenEmpty>
      {!mapToSearchFilterCategories && <ExperiencesPassportOfferSummary offer={offer} filters={filters} variant="search-tile" />}
      {luxPlusLPPEnabled && !hideLuxPlusPricingSheet && <LuxLPPPricingLabeledSheet
        offer={offer}
        hasLuxPlusRate={directSearchPrices?.hasLuxPlusRate}
        offerMemberPrice={offerMemberPrice}
        offerPrice={offerPrice}
      />}
      {isUsingMappedRateInclusions && (!!displayedMappedInclusions.length || !!inclusionOverflowLabel) && <SearchTileStackInclusionsList
        inclusions={displayedMappedInclusions}
        truncateAfter={getCompactInclusionListLimit(mappedInclusionLimit, !!cancellationInclusion)}
        overflowLabel={inclusionOverflowLabel}
        variant="small"
        lineClamp={1}
        onShowMoreClick={openAllInclusionsModal}
      />}
      {!isUsingMappedRateInclusions && <Group direction="vertical" gap={4} noDisplayWhenEmpty>
        {cancellationInclusionKind && <SearchTileFreeCancellationInclusion kind={cancellationInclusionKind} />}
        {!!amenities.length && <SearchTileStackAmenitiesList
          amenities={amenities}
          truncateAfter={truncateAmenitiesAfter}
          singleColumn={singleColumn}
          variant={amenitiesListVariant}
        />}
        {!mapToSearchFilterCategories && !hideLoyaltyInclusions && <LoyaltyInclusions offer={offer} variant="search-tile" />}
      </Group>}
    </Group>
    {!hideRoomBreakdown && <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
    />}
  </Group>
}

export default BedbankSearchTileFeatures
